import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const DEFAULTS = {
  overline: "About Royale Yaan",
  hero_title: "A house built on the love of extraordinary machines.",
  hero_emphasis: "extraordinary machines.",
  story_overline: "Our Story",
  story_heading: "Curated for the connoisseur.",
  story_paragraphs: [""],
  values: [{ n: "", l: "" }],
  cta_heading: "Begin a conversation.",
};

export default function AboutEditor() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/about")
      .then((r) => setForm(r.data))
      .catch(() => setForm(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setParagraph = (i, v) => {
    const arr = [...form.story_paragraphs];
    arr[i] = v;
    set("story_paragraphs", arr);
  };
  const addParagraph = () => set("story_paragraphs", [...form.story_paragraphs, ""]);
  const removeParagraph = (i) => set("story_paragraphs", form.story_paragraphs.filter((_, j) => j !== i));

  const setValue = (i, k, v) => {
    const arr = form.values.map((x, j) => (j === i ? { ...x, [k]: v } : x));
    set("values", arr);
  };
  const addValue = () => set("values", [...form.values, { n: "", l: "" }]);
  const removeValue = (i) => set("values", form.values.filter((_, j) => j !== i));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        story_paragraphs: form.story_paragraphs.map((s) => s.trim()).filter(Boolean),
        values: form.values.filter((v) => v.n.trim() || v.l.trim()),
      };
      await api.put("/admin/content/about", payload);
      toast.success("About page updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-gray-500">Loading…</div>;

  return (
    <form onSubmit={save} className="space-y-8" data-testid="about-editor">
      <div className="bg-white border border-gray-200 p-6 md:p-8 space-y-5">
        <p className="overline">Hero</p>
        <Field label="Overline">
          <Input data-testid="about-overline" value={form.overline} onChange={(e) => set("overline", e.target.value)} />
        </Field>
        <Field label="Hero title">
          <Textarea rows={2} data-testid="about-hero-title" value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)} />
        </Field>
        <Field label="Hero emphasis (rendered in gold — must appear inside hero title)">
          <Input data-testid="about-hero-emphasis" value={form.hero_emphasis} onChange={(e) => set("hero_emphasis", e.target.value)} />
        </Field>
      </div>

      <div className="bg-white border border-gray-200 p-6 md:p-8 space-y-5">
        <p className="overline">Story section</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Story overline">
            <Input data-testid="about-story-overline" value={form.story_overline} onChange={(e) => set("story_overline", e.target.value)} />
          </Field>
          <Field label="Story heading">
            <Input data-testid="about-story-heading" value={form.story_heading} onChange={(e) => set("story_heading", e.target.value)} />
          </Field>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.15em] text-gray-600">Paragraphs</Label>
          <div className="mt-2 space-y-3">
            {form.story_paragraphs.map((p, i) => (
              <div key={i} className="flex gap-2 items-start">
                <Textarea
                  rows={3}
                  data-testid={`about-paragraph-${i}`}
                  value={p}
                  onChange={(e) => setParagraph(i, e.target.value)}
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeParagraph(i)} className="mt-1">
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addParagraph} data-testid="about-add-paragraph" className="gap-2">
              <Plus size={14} /> Add paragraph
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 p-6 md:p-8 space-y-5">
        <p className="overline">Stats / values strip</p>
        <div className="space-y-3">
          {form.values.map((v, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-center">
              <div className="col-span-4">
                <Input placeholder="Number (e.g. 200+)" data-testid={`about-value-n-${i}`} value={v.n} onChange={(e) => setValue(i, "n", e.target.value)} />
              </div>
              <div className="col-span-7">
                <Input placeholder="Label (e.g. Cars curated)" data-testid={`about-value-l-${i}`} value={v.l} onChange={(e) => setValue(i, "l", e.target.value)} />
              </div>
              <div className="col-span-1 text-right">
                <Button type="button" variant="ghost" size="sm" onClick={() => removeValue(i)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addValue} data-testid="about-add-value" className="gap-2">
            <Plus size={14} /> Add stat
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 p-6 md:p-8 space-y-5">
        <p className="overline">Closing CTA</p>
        <Field label="CTA heading">
          <Input data-testid="about-cta" value={form.cta_heading} onChange={(e) => set("cta_heading", e.target.value)} />
        </Field>
      </div>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setForm(DEFAULTS)}
          data-testid="about-reset"
        >
          Reset to defaults
        </Button>
        <Button type="submit" disabled={saving} data-testid="about-save" className="btn-primary rounded-sm px-10 h-12">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-gray-600">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
